import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const BASE = process.env.PUBLIC_URL || '';

/* ============================================================
   SCROLL ANIMATION HOOK
   ============================================================ */
function useScrollAnimations() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = document.querySelectorAll('.animate-on-scroll');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ============================================================
   APPS DATA
   ============================================================ */
const APPS = [
  {
    id: 'instant-guide',
    name: 'Instant Guide',
    description:
      'Deep audio tours with stories, secrets, history, and fun facts for 11,000+ landmarks.',
    coverImg: `${BASE}/app_icon.jpg`,
    ctaClass: 'app-card__cta--ig',
    platforms: ['iOS', 'Android'],
    category: 'Travel & Navigation',
    link: '/apps/instant-guide',
    badge: null,
  },
  {
    id: 'chain-link-pro',
    name: 'Chain Link Pro',
    description:
      'The award-winning puzzle game featured by Apple under "Hot New Games" and "What\'s Hot." Link matching colored shapes toward a center vortex. Over 400,000 downloads.',
    coverImg: `${BASE}/clp_icon.png`,
    ctaClass: 'app-card__cta--clp',
    platforms: ['iOS'],
    category: 'Puzzle Game',
    link: '/apps/chain-link-pro',
    badge: '★ Apple Featured',
  },
  {
    id: 'chain-link-pro-2',
    name: 'Chain Link Pro 2',
    description:
      'The long-awaited sequel to the Apple-featured Chain Link Pro. More game modes, more power-ups, and even more addictive chain reaction puzzle action.',
    coverImg: `${BASE}/clp2_icon.png`,
    ctaClass: 'app-card__cta--clp2',
    platforms: ['iOS'],
    category: 'Puzzle Game',
    link: '/apps/chain-link-pro-2',
    badge: null,
  },
];

/* ============================================================
   HERO SECTION
   ============================================================ */
function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="hero">
      <div className="hero__orb hero__orb--blue" />
      <div className="hero__orb hero__orb--purple" />

      <div className="hero__content">
        <h1 className="hero__title">
          Software{' '}
          <span className="gradient-text">Beyond Limits</span>
        </h1>

        <p className="hero__subtitle">
          Creating innovative mobile applications that are fun to use.
        </p>

        <div className="hero__cta-wrap">
          <button className="btn-primary" onClick={() => scrollTo('apps')}>
            Explore Apps
            <span className="btn-arrow">→</span>
          </button>
        </div>

        <div className="hero__stats">
          <div className="hero__stat">
            <div className="hero__stat-value">400,000+</div>
            <div className="hero__stat-label">App Downloads</div>
          </div>
          <div className="hero__stat">
            <div className="hero__stat-value">15+</div>
            <div className="hero__stat-label">Years Experience</div>
          </div>
        </div>
      </div>

      <div className="hero__scroll-indicator">
        <div className="hero__scroll-dot" />
      </div>
    </section>
  );
}

/* ============================================================
   APPS SECTION
   ============================================================ */
function AppsSection() {
  return (
    <section id="apps" className="apps-section">
      <div className="section-header animate-on-scroll">
        <p className="section-eyebrow">Our Portfolio</p>
        <h2 className="section-title">
          Featured <span className="gradient-text">Apps</span>
        </h2>
        <p className="section-subtitle">
          Discover our collection of mobile applications designed to enhance
          your daily life and keep you entertained.
        </p>
      </div>

      <div className="apps-grid">
        {APPS.map((app, i) => (
          <Link
            to={app.link}
            key={app.id}
            className={`app-card animate-on-scroll stagger-${i + 1}`}
          >
            {/* Cover area */}
            <div className="app-card__cover">
              <img
                src={app.coverImg}
                alt={app.name}
                className="app-card__cover-img"
              />

              {/* Featured badge */}
              {app.badge && (
                <div className="app-card__featured-badge">{app.badge}</div>
              )}
            </div>

            {/* Body */}
            <div className="app-card__body">
              <h3 className="app-card__name">{app.name}</h3>

              <div className="app-card__meta">
                <span>{app.category}</span>
              </div>

              <p className="app-card__desc">{app.description}</p>

              <div className="app-card__platforms">
                {app.platforms.map((p) => (
                  <span key={p} className="platform-badge">{p}</span>
                ))}
              </div>

              <div className={`app-card__cta ${app.ctaClass}`}>
                <span>View App Details</span>
                <span>→</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   ABOUT SECTION
   ============================================================ */
function AboutSection() {
  return (
    <section id="about" className="about-section">
      <div className="about-grid">
        <div className="animate-on-scroll animate-from-left">
          <h2 className="about-title">
            About{' '}
            <span className="gradient-text">The Developer</span>
          </h2>
          <p className="about-body">
            Hi! I'm Joshua Levine, an independent mobile app developer who wears many hats
            in all aspects of the software development lifecycle, including project planning,
            interface design, software engineering, testing, marketing, and customer support.
          </p>
          <p className="about-body">
            With over 400,000 app downloads across multiple platforms, I focus on
            delivering high-quality, user-friendly mobile experiences. Each app is
            crafted with care, from concept to deployment, ensuring the best
            possible experience for users.
          </p>

          <div className="about-values">
            <div className="value-card">
              <div className="value-icon">🚀</div>
              <div className="value-title">Polished UI</div>
              <p className="value-desc">
                Every detail refined until the app feels effortless to use
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon">📱</div>
              <div className="value-title">User-Focused</div>
              <p className="value-desc">
                Apps designed with the end user in mind
              </p>
            </div>
            <div className="value-card">
              <div className="value-icon">♥</div>
              <div className="value-title">Passionate</div>
              <p className="value-desc">
                Built with dedication and attention to detail
              </p>
            </div>
          </div>
        </div>

        <div className="about-image-wrap animate-on-scroll animate-from-right">
          <div className="about-orb-1" />
          <div className="about-orb-2" />
          <div className="about-image">
            <img
              src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=720&q=80"
              alt="Software development workspace"
              loading="lazy"
            />
            <div className="about-image__overlay" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   HOME PAGE
   ============================================================ */
function Home() {
  useScrollAnimations();

  return (
    <>
      <Hero />
      <AppsSection />
      <AboutSection />
    </>
  );
}

export default Home;
